/**
 * Shared helpers for the focused single-task Add flows.
 *
 * See docs/add-task-flows.md for the design. These keep each flow page thin and
 * consistent: period derivation, the "remember-GL" lookup, and settings load.
 */

import { getDb } from "../db";
import { saveCgmReading, saveEntry } from "../db/operations";
import { calculateIOB } from "../config/dafne/insulin";
import type { LogEntry, PeriodTag, UserSettings } from "../types";
import {
  DEFAULT_MEAL_TIMES,
  DEFAULT_SETTINGS,
  getQATotal,
  suggestPeriodTagSmart,
} from "../types";

/** How long a recent glucose stays usable for pre-fill across flows (minutes). */
export const REMEMBER_GL_WINDOW_MINUTES = 20;

/** Coerce a Date | string | number timestamp to epoch millis. */
function toMillis(t: Date | string | number | unknown): number {
  if (t instanceof Date) return t.getTime();
  if (typeof t === "number") return t;
  return new Date(t as string).getTime();
}

/**
 * Derive the DAFNE period tag for a timestamp from the user's meal times.
 * Used so focused flows can default the period without showing the picker.
 */
export function derivePeriodTag(
  timestamp: Date,
  settings: UserSettings,
): PeriodTag {
  return suggestPeriodTagSmart(
    timestamp,
    settings.mealTimes ?? DEFAULT_MEAL_TIMES,
  ).periodTag;
}

export interface RecentGlucose {
  glucose: number; // mmol/L
  timestamp: Date;
  ageMinutes: number; // whole minutes since the reading
}

/**
 * Most recent glucose within `windowMinutes`, across manual CGM readings and
 * logged entries with a `bloodGlucose`. Powers the "remember-GL" pre-fill: tap
 * `+`, enter a reading, tap `+` again soon after and the next flow reuses it.
 *
 * Returns null when nothing is recent enough - past the window DAFNE would not
 * trust the value for a correction, so the flow prompts fresh.
 */
export async function getRecentGlucose(
  windowMinutes = REMEMBER_GL_WINDOW_MINUTES,
): Promise<RecentGlucose | null> {
  const now = Date.now();
  const windowStart = now - windowMinutes * 60 * 1000;

  let bestTs = -1;
  let bestGl = 0;

  try {
    const db = getDb();
    const readings = await db.cgmReadings.toArray();
    for (const r of readings) {
      if ((r as { _deletedAt?: unknown })._deletedAt) continue;
      const ts = toMillis(r.timestamp);
      if (ts < windowStart || ts > now) continue;
      if (ts > bestTs && r.glucose > 0) {
        bestTs = ts;
        bestGl = r.glucose;
      }
    }

    const entries = await db.entries.toArray();
    for (const e of entries) {
      if ((e as { _deletedAt?: unknown })._deletedAt) continue;
      if (e.bloodGlucose === undefined || e.bloodGlucose <= 0) continue;
      const ts = toMillis(e.timestamp);
      if (ts < windowStart || ts > now) continue;
      if (ts > bestTs) {
        bestTs = ts;
        bestGl = e.bloodGlucose;
      }
    }
  } catch (error) {
    console.error("getRecentGlucose failed:", error);
    return null;
  }

  if (bestTs < 0) return null;
  return {
    glucose: bestGl,
    timestamp: new Date(bestTs),
    ageMinutes: Math.max(0, Math.round((now - bestTs) / 60000)),
  };
}

/**
 * Persist a focused single-task entry as a "live" log entry. Derives the
 * period from `when` unless `fields.periodTag` overrides it, and stamps the
 * live-logging context fields. Returns the new entry id.
 */
export async function saveFocusedEntry(
  fields: Partial<Omit<LogEntry, "id" | "createdAt" | "updatedAt">>,
  settings: UserSettings,
  when: Date = new Date(),
): Promise<string> {
  const periodTag = derivePeriodTag(when, settings);
  return saveEntry({
    timestamp: when,
    periodTag,
    entryContext: "live",
    loggedAt: when,
    minutesLag: 0,
    source: "manual",
    ...fields,
  });
}

/**
 * Persist a freshly-entered glucose as a manual CGM reading so it reaches the
 * glucose charts, day stats and pattern analysis. Flows that capture a new GL
 * call this in addition to storing `bloodGlucose` on the LogEntry (which is
 * what the report diary grid reads). The two are read by disjoint consumers,
 * so there is no double-count. See docs/add-task-flows.md.
 */
export async function saveManualGlucoseReading(
  glucose: number,
  when: Date = new Date(),
): Promise<void> {
  await saveCgmReading({
    timestamp: when,
    glucose,
    type: "manual",
    source: "manual",
    createdAt: when,
  });
}

/** Load user settings, falling back to defaults if none are stored yet. */
export async function loadUserSettings(): Promise<UserSettings> {
  try {
    const stored = await getDb().settings.get("settings");
    if (stored) return stored;
  } catch (error) {
    console.error("loadUserSettings failed:", error);
  }
  return { ...DEFAULT_SETTINGS };
}

/**
 * Active quick-acting insulin on board over the last few hours, plus the
 * portion attributable to corrections (insulin without carbs). Mirrors the
 * calculation in quick-gl-page so correction suggestions account for IOB.
 */
export async function getActiveIob(): Promise<{
  total: number;
  correction: number;
}> {
  const now = Date.now();
  const windowStart = now - 5 * 60 * 60 * 1000;

  let total = 0;
  let correction = 0;
  try {
    const entries = await getDb().entries.toArray();
    for (const e of entries) {
      if ((e as { _deletedAt?: unknown })._deletedAt) continue;
      const ts = toMillis(e.timestamp);
      if (ts < windowStart || ts > now) continue;
      const qaTotal = getQATotal(e);
      if (!qaTotal || qaTotal <= 0) continue;
      const hoursElapsed = (now - ts) / (1000 * 60 * 60);
      const iob = calculateIOB(qaTotal, hoursElapsed);
      total += iob;
      const hasCarbs = (e.carbs ?? 0) > 0;
      if (!hasCarbs) {
        correction += iob;
      } else if (e.qaCorrection && e.qaCorrection > 0) {
        correction += calculateIOB(e.qaCorrection, hoursElapsed);
      }
    }
  } catch (error) {
    console.error("getActiveIob failed:", error);
  }

  return {
    total: Math.round(total * 10) / 10,
    correction: Math.round(correction * 10) / 10,
  };
}
